/**
 * トークンの置き場。アクセストークンはメモリだけ、リフレッシュトークンは localStorage（04 §6.2 の Web 版）。
 */
const REFRESH_KEY = 'saas.refreshToken';
const TENANT_KEY = 'saas.tenantSlug';

let accessToken: string | null = null;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export const tokenStore = {
  getAccess: (): string | null => accessToken,
  setAccess: (t: string | null): void => {
    accessToken = t;
  },
  getRefresh: (): string | null => read(REFRESH_KEY),
  setRefresh: (t: string | null): void => write(REFRESH_KEY, t),
  clear: (): void => {
    accessToken = null;
    write(REFRESH_KEY, null);
  },
};

/** 親ドメイン（ビルド時の VITE_BASE_DOMAIN。API の BASE_DOMAIN と同じ値）。未設定なら「3 ラベル以上なら先頭がテナント」の経験則 */
const BASE_DOMAIN: string | null = (import.meta.env.VITE_BASE_DOMAIN ?? '').trim().toLowerCase() || null;

/**
 * テナントの決め方（02 §3.1）: 本番はサブドメイン <slug>.BASE_DOMAIN。ローカル（localhost / IP）は X-Tenant ヘッダ
 * （API の ALLOW_TENANT_HEADER=true）。BASE_DOMAIN そのもので開かれたとき（単一ドメイン運用）は null を返し、
 * ログイン画面で slug を入力してもらう（API には body.tenantSlug / ?slug= で渡す）。
 */
export function tenantSlugFromHost(hostname = window.location.hostname): string | null {
  const host = hostname.toLowerCase();
  if (host === 'localhost' || /^[0-9.]+$/.test(host) || host.endsWith('.localhost')) {
    // app.localhost のような形は許す（ブラウザは *.localhost を 127.0.0.1 に解決する）
    const sub = host.endsWith('.localhost') ? host.slice(0, -'.localhost'.length) : null;
    return sub && !sub.includes('.') ? sub : null;
  }
  if (BASE_DOMAIN) {
    if (host === BASE_DOMAIN || !host.endsWith('.' + BASE_DOMAIN)) return null;
    const sub = host.slice(0, -(BASE_DOMAIN.length + 1));
    return sub && !sub.includes('.') ? sub : null;
  }
  const parts = host.split('.');
  if (parts.length >= 3 && parts[0] && parts[0] !== 'www') return parts[0];
  return null;
}

export const tenantStore = {
  /** ホストから決まるなら常にそれ。決まらなければ保存済みの slug */
  current: (): string | null => tenantSlugFromHost() ?? read(TENANT_KEY),
  fromHost: (): string | null => tenantSlugFromHost(),
  stored: (): string | null => read(TENANT_KEY),
  remember: (slug: string | null): void => write(TENANT_KEY, slug),
  /** サブドメインで決まらない環境か（ログイン画面に slug 欄を出す） */
  needsHeader: (): boolean => tenantSlugFromHost() === null,
};
